import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoistureReportObservationsInput } from 'GQL';
import InfoText from './InfoText';
import TextAreaInput from './TextAreaInput';

interface Props {
    observations: MoistureReportObservationsInput;
    onChange(observations: MoistureReportObservationsInput): void;
}

const MoistureReportObservationsFromV2: React.FC<Props> = ({ observations, onChange }) => {
    const { t } = useTranslation();

    function handleChange(updateFn: (copy: MoistureReportObservationsInput) => void) {
        const copy = { ...observations };
        updateFn(copy);
        onChange(copy);
    }

    return (
        <>
            <InfoText className='mt-4' title='reports.contacts'>
				<p>Kontaktpersoner noteres I dette felt. (kan oftest blot kopieres fra Scalepoint)</p>
				<ol className='list-decimal list-inside'>
					<li>Rolle (Forsikringstager)</li>
					<li>Navn</li>
					<li>Telefon</li>
					<li>Mail</li>
				</ol>
				<p>Felt bliver genereret for hvor etage der vælges i rapport, hvorfor der ved flere lejligheder kun noteres beboer pr. lejlighed der generes i rapport. </p>
			</InfoText>
            <TextAreaInput
                label=''
                placeholder={t('reports.contactsPlaceholder')}
                value={observations.contacts ?? ''}
                onChange={value => handleChange(copy => (copy.contacts = value))}
            />

            <InfoText className='mt-4' title='reports.moistureReportObservations.construction'>
				<ul className='list-disc list-inside'>
					<li>Berørte og tilstødende konstruktioner beskrives.</li>
					<li>Konstruktionen beskrives indefra og ud i kronologisk rækkefølge.</li>
					<li>Er konstruktionens opbygning ukendt og relevant for udbedringen (f.eks. affugtning), søges oplysninger i kommunens byggesagsarkiv (f.eks. via Google: "Aalborg byggesagsarkiv"). Oplysningerne er vejledende og kan afvige fra den faktiske konstruktion. Ved behov for sikker afklaring undersøges konstruktionen ved destruktive indgreb efter godkendelse fra forsikringsselskab eller kunde.</li>
					<li>Er konstruktionen ikke relevant for skaden, beskrives kun den synlige del, og det noteres, at konstruktionen ikke har betydning for skaden.</li>
					<li>Ved forskellige konstruktioner beskrives hver konstruktion og hvilke rum.</li>
					<li>Brug så vidt muligt de korrekte termer for materialerne.</li>
				</ul>
			</InfoText>

            <TextAreaInput
                label={t('reports.moistureReportObservations.ceiling')}
                placeholder={t('reports.moistureReportObservations.ceilingPlaceholder')}
                value={observations.ceiling ?? ''}
                onChange={value => handleChange(copy => (copy.ceiling = value))}
            />

            <InfoText className='mt-4' title='reports.moistureReportObservations.walls'>
				<ul className='list-disc list-inside'>
					<li>Inder- og ydervægge noteres (altså både facade, bagmur og skillevægge)</li>
				</ul>
			</InfoText>
            <TextAreaInput
                label=''
                placeholder={t('reports.moistureReportObservations.wallsPlaceholder')}
                value={observations.walls ?? ''}
                onChange={value => handleChange(copy => (copy.walls = value))}
            />

            <InfoText className='mt-4' title='reports.moistureReportObservations.floor'>
				<ul className='list-disc list-inside'>
					<li>Et trægulv er ikke blot et trægulv. Er det parket, massiv eller laminat.</li>
					<li>Er gulvet limet, flydende/svømmende gulv eller opstrøget.</li>
				</ul>
			</InfoText>
            <TextAreaInput
                label=''
                placeholder={t('reports.moistureReportObservations.floorPlaceholder')}
                value={observations.floor ?? ''}
                onChange={value => handleChange(copy => (copy.floor = value))}
            />

            <InfoText className='mt-4' title='reports.moistureReportObservations.scope'>
				<ul className='list-disc list-inside'>
					<li>Opsætning rum for rum med angivelse af m² på rummende samt skaderamt areal.</li>
					<li>Beskrivelse af omfang i direkte berørte rum.</li>
					<li>Hvad er berørt (beskrivelse af følgeskaderne)</li>
					<li>Er der undersøgt i andre rum, uden der er berørt omfang. Noteres det blot hvilke rum der er undersøgt men at der ikke er konstateret noget. </li>
				</ul>
			</InfoText>
            <TextAreaInput
                label=''
                placeholder={t('reports.moistureReportObservations.scopePlaceholder')}
                value={observations.scope ?? ''}
                onChange={value => handleChange(copy => (copy.scope = value))}
            />

            <InfoText className='mt-4' title='reports.moistureReportObservations.recommendations'>
				<ul className='list-disc list-inside'>
					<li>Opsætning rum for rum i kronologisk rækkefølge.</li>
					<li>Beskrivelse af tiltag der skal udføres.</li>
					<li>Angiv mængder for de enkelte tiltag (f.eks. m² tapet eller gulv, der skal fjernes).</li>
					<li>Tag stilling til affugtning og beskriv den valgte affugtningsstrategi (styrker forståelsen af hvilken affugtning metode vi prissætter).</li>
					<li>Hvem udfører hvad?</li>
				</ul>
			</InfoText>
            <TextAreaInput
                label=''
                placeholder={t('reports.moistureReportObservations.recommendationsPlaceholder')}
                value={observations.recommendations ?? ''}
                onChange={value => handleChange(copy => (copy.recommendations = value))}
            />

            <InfoText className='mt-4' title='reports.moistureReportObservations.movables'>
				<ol className='list-decimal list-inside'>
					<li>Er der skade på løsøre?</li>
					<li>Skal løsøret fjernes for at komme til bygningsskaden?</li>
					<li>Hvor meget handler det om? Antal flyttekasser og/eller m³ møbler.</li>
					<li>Kan det blive på forsikringsstedet eller flyttes til container</li>
					<li>Hvis kunden selv nedpakker, leverer I så kasser, og hvor mange?</li>
				</ol>
			</InfoText>
            <TextAreaInput
                label=''
                placeholder={t('reports.moistureReportObservations.movablesPlaceholder')}
                value={observations.movables ?? ''}
                onChange={value => handleChange(copy => (copy.movables = value))}
            />
        </>
    );
};

export default MoistureReportObservationsFromV2;